/**
 * The four honest answers to "what happened to that search?", plus the two
 * context fields needed to tell the surfaces apart.
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 *
 * The chat can tell a shopper it found nothing when the search never actually
 * ran. That happened in production: a thread whose results hash already held
 * 32 products rendered the seed turn's "ask me anything about it" fallback —
 * the copy `buildSeedDisplayContent` emits for zero matches — and the shopper
 * only saw the grid after typing "where are the results?".
 *
 * Nothing recorded it, and nothing could have. `Performed Dupe` only fires on
 * a product-bearing turn (its taxonomy lock spells that rule out:
 * `performedDupeEmitterInventory.spec.ts`), `Search No Results` only fires
 * when a `search` tool call came back empty, and browser `logger.*` never
 * reaches Datadog because RUM's session sample rate is 0. A search that was
 * never ENABLED produces none of those.
 *
 * So the one distinction this module exists to preserve:
 *
 *   `zero`      the search ran and genuinely returned nothing
 *   `never_ran` the search was never started at all
 *
 * ⛔ Never collapse `never_ran` into `zero`. That collapse IS the defect being
 * measured — once the two share a value, no query can separate "we looked and
 * there is nothing" from "we never looked", which is exactly the state the
 * code was already in.
 */

import { isInIframe } from '~/src/lib/isInIframe'

/**
 * What happened to a dupes search, as known at the moment the event fires.
 *
 * - `results`   — it ran and returned products.
 * - `zero`      — it ran, settled, and returned none.
 * - `failed`    — it ran and errored. The counts are UNKNOWN, not empty.
 * - `never_ran` — it was never enabled. Nothing was requested; no server ever
 *                 saw this search.
 * - `unknown`   — it was enabled and had not settled by the time we had to
 *                 fire. Honest ignorance, and deliberately its own value so it
 *                 cannot be mistaken for any of the four real states.
 */
export type SearchOutcomeState =
  | 'results'
  | 'zero'
  | 'failed'
  | 'never_ran'
  | 'unknown'

export interface SearchOutcomeInput {
  /**
   * Whether the query was ever ENABLED — i.e. whether its gates were all
   * satisfied so a request could be issued.
   *
   * ⚠️ This is the field `isLoading` cannot give you. A disabled TanStack v5
   * query is `isPending: true, isFetching: false`, and
   * `isLoading = isPending && isFetching` (verified in the installed
   * `@tanstack/query-core`), so a query that has never run reports
   * `isLoading: false`. On the exact bug this module measures, "not loading"
   * is true while nothing has happened — so the enabled gates have to be
   * evaluated directly by the caller, from the same inputs the query gates on.
   */
  enabled: boolean
  /**
   * POSITIVE proof the query finished: data arrived, or an error arrived.
   *
   * ⚠️ Must never be `!isLoading`. Two separate reasons, and they compound.
   * `!isLoading` is true in the tick BEFORE a request starts (documented at
   * the `/[hash]` call site, caught in a browser), and it is also true for
   * the whole life of a query that was never enabled. Either one alone
   * reports a finished zero-result search that never happened.
   */
  settled: boolean
  /** The query errored. Implies settled; takes precedence over a zero count. */
  failed: boolean
  /**
   * Products the search produced, or `null` when the caller cannot count them.
   * `null` reads as unknown, never as zero.
   */
  resultsCount: number | null
}

/**
 * Collapse a search's state into one of the five values above.
 *
 * Order matters and is deliberate:
 *
 * 1. Products on screen settle it — whatever the flags say, something ran.
 * 2. Not enabled → `never_ran`, before any count is consulted. A disabled
 *    query's count is 0 and its `isLoading` is false, so consulting either
 *    first is how `never_ran` becomes `zero`.
 * 3. An error means the counts are unknown, not empty.
 * 4. Unsettled, or an uncountable surface, is `unknown`.
 * 5. Only then is a zero a real zero.
 */
export function deriveSearchOutcome({
  enabled,
  settled,
  failed,
  resultsCount,
}: SearchOutcomeInput): SearchOutcomeState {
  if (resultsCount !== null && resultsCount > 0) return 'results'
  if (!enabled) return 'never_ran'
  if (failed) return 'failed'
  if (!settled || resultsCount === null) return 'unknown'
  return 'zero'
}

/**
 * Where the chat was rendering when the event fired.
 *
 * The same chat renders in at least three places and they behave differently,
 * so a single pooled number hides whichever one is broken.
 *
 * Derived from the PATHNAME rather than from a React prop or a context value,
 * because the pathname is the one thing every surface agrees on and it is
 * readable synchronously at fire time. In particular it is what separates the
 * extension side panel (dupe.com in a cross-origin iframe inside a
 * `chrome-extension://` page) from any other embed — `in_iframe` alone cannot,
 * since any site can frame us.
 */
export type SearchSurface =
  | 'sidepanel'
  | 'extension'
  | 'chat'
  | 'product_page'
  | 'results_page'
  | 'home'
  | 'other'

/**
 * Is this single path segment a `results.hash` rather than a page name?
 *
 * Two shapes count: a bare md5 of the source product URL, or a short id in the
 * same alphabet that carries a capital (`YrGCYhFhd`). The capital is the
 * discriminator — every real top-level route in this app is lowercase kebab,
 * so without it `/collections` and `/sweepstakes` would both be filed as the
 * legacy results page.
 */
const isResultsHashSegment = (segment: string): boolean =>
  /^[0-9a-f]{32}$/.test(segment) ||
  (/^[0-9a-zA-Z]{6,40}$/.test(segment) && /[A-Z]/.test(segment))

/**
 * Classify a pathname into a surface.
 *
 * The side-panel test comes first and is deliberately broad: three different
 * routes serve it (`/extension/sidepanel-chat`, `/chats/<hash>/sidepanel`,
 * `/<hash>/sidepanel`) and a fourth would otherwise land silently in `chat`.
 *
 * `results_page` is the legacy `/<hash>` results page. It is matched last, and
 * only when the one segment actually LOOKS like a results hash — see
 * `isResultsHashSegment`. Anything else single-segment is `other`, because a
 * surface label that quietly absorbs every future top-level page is worse than
 * no label.
 */
export function searchSurfaceFromPath(pathname: string): SearchSurface {
  const path = (pathname || '').split(/[?#]/)[0] ?? ''
  const segments = path.split('/').filter(Boolean)

  if (segments.length === 0) return 'home'
  const last = segments[segments.length - 1]
  if (
    last === 'sidepanel' ||
    last === 'sidepanel-chat' ||
    last === 'sidepanel-dupe'
  ) {
    return 'sidepanel'
  }
  if (segments[0] === 'extension') return 'extension'
  if (segments[0] === 'chats') return 'chat'
  if (segments[0] === 'product') return 'product_page'
  // Only a segment that looks like a results hash — never every single-segment
  // route, which would absorb `/steals`, `/collections` and anything added next.
  if (segments.length === 1 && isResultsHashSegment(segments[0] ?? '')) {
    return 'results_page'
  }
  return 'other'
}

/**
 * The surface, read from `window.location` AT CALL TIME.
 *
 * Not captured on mount: these events fire from poll callbacks and settle
 * deadlines seconds later, and a client-side navigation re-renders the same
 * component rather than remounting it.
 */
export function readSearchSurface(): SearchSurface | null {
  if (typeof window === 'undefined') return null
  try {
    return searchSurfaceFromPath(window.location.pathname)
  } catch {
    return null
  }
}

/**
 * The two context fields every search-outcome event carries, both read at the
 * moment the event is built.
 *
 * ⛔ `in_iframe` comes from `isInIframe()`, deliberately NOT the `useInIframe`
 * hook. That hook sets its value in an effect, so it reads `false` on first
 * render and only corrects a tick later — late enough that gating on it has
 * already shipped a bug here (the side-panel broadcast). `isInIframe` is the
 * synchronous predicate DUP-2356 established for exactly this, down to
 * treating a cross-origin throw as proof we ARE framed.
 */
export function readSearchContextProps(): {
  surface: SearchSurface | null
  in_iframe: boolean | null
} {
  if (typeof window === 'undefined') return { surface: null, in_iframe: null }
  return { surface: readSearchSurface(), in_iframe: isInIframe() }
}
